import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AlertTriangle, CloudCheck } from "lucide-react";

export function BackupHealthBadge({ dark = true }) {
  const { has } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  const canSee = has("manage_backups");

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    const fetch = () => api.get("/admin/backups/offsite").then(({ data }) => alive && setStatus(data)).catch(() => {});
    fetch();
    const t = setInterval(fetch, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [canSee]);

  if (!canSee || !status || !status.configured) return null;

  if (status.stale) {
    return (
      <button
        data-testid="nav-backup-stale"
        onClick={() => navigate("/backups")}
        title={status.hours_since_success != null
          ? `Last off-site backup ${status.hours_since_success}h ago (alerts after ${status.stale_after_hours}h)`
          : "No successful off-site backup on record"}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#B01E28] text-white text-[10px] font-mono font-bold uppercase tracking-wide px-2.5 h-7 hover:bg-[#931822] transition-colors animate-pulse"
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Backup overdue</span>
      </button>
    );
  }

  // healthy — subtle, unobtrusive
  return (
    <button
      data-testid="nav-backup-ok"
      onClick={() => navigate("/backups")}
      title={status.hours_since_success != null
        ? `Off-site backup healthy — last success ${status.hours_since_success}h ago`
        : "Off-site backup healthy"}
      className={`hidden md:inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${dark ? "text-emerald-400/70 hover:text-emerald-300 hover:bg-white/10" : "text-emerald-600/70 hover:text-emerald-700 hover:bg-slate-100"}`}
    >
      <CloudCheck className="w-4 h-4" />
    </button>
  );
}
